import DocumentTextField from "components/foundation/DocumentTextField/DocumentTextField";
import React, { useEffect, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { TempDocumentDetailResponse } from "apis/data-contracts";
import styled from "styled-components";
import Divider from "components/foundation/Divider";
import CheckBox from "components/foundation/CheckBox/CheckBox";
import Select from "components/foundation/Select/Select";
import CreateDocumentForm from "../../CreateDocumentForm";
import CreateDocumentClassFormBody from "../CreateDocumentClassFormBody";
import { CONTRACT_INFO_STEP2_FIELD as FIELD } from "config/documentConstant";
import formatMinMaxLengthMessage from "utils/formatUtils/formatMinMaxLengthMessage";
import formatNumber from "utils/formatUtils/formatNumber";
import { get } from "lodash-es";
import DocumentTextAreaField from "components/foundation/DocumentTextField/DocumentTextArea";

/* 수주정보관리 2단계 문서 */
interface ContractInfoStep2Form {
  [FIELD.BASIC_REVIEW.fields.LOCATION.key]: string;
  [FIELD.BASIC_REVIEW.fields.BUSINESS_RIGHT_ACQUISITION.key]: string;
  [FIELD.BASIC_REVIEW.fields.PERMISSION.key]: string;
  [FIELD.BASIC_REVIEW.fields.MARKET_STATUS.key]: string;
  [FIELD.BASIC_REVIEW.fields.APPROPRIATE_SALE_PRICE.key]: string;
  [FIELD.PROFITABILITY_UNITS.key]: Record<string, string>;

  [FIELD.PROFITABILITY.key]: {
    category: string;
    amount: number;
    note: string;
  }[];
  [FIELD.REVIEW_RESULT.key]: string;
  [FIELD.PROCESSING_RESULT.key]: string;
}

type ProfitabilityFormRows =
  ContractInfoStep2Form[typeof FIELD.PROFITABILITY.key];

interface ContractInfoProps {
  tempDocumentDetail?: TempDocumentDetailResponse;
}

const DEFAULT_AMOUNT_UNITS: Record<string, string> = {
  0: "1",
  1: "1",
  2: "1",
  3: "1",
  4: "1",
  5: "1",
  6: "1",
};

const resolveSharedAmountUnit = (
  units?: Record<string, string> | null,
): string => {
  return (
    units?.["0"] ||
    Object.values(units || {}).find(
      (value): value is string => typeof value === "string" && value.length > 0,
    ) ||
    DEFAULT_AMOUNT_UNITS["0"]
  );
};

const normalizeAmountUnits = (
  units?: Record<string, string> | null,
): Record<string, string> => {
  const sharedUnit = resolveSharedAmountUnit(units);

  return Object.keys(DEFAULT_AMOUNT_UNITS).reduce(
    (acc, key) => ({
      ...acc,
      [key]: sharedUnit,
    }),
    {} as Record<string, string>,
  );
};

const normalizeProfitabilityRows = (
  rows?: ProfitabilityFormRows,
): ProfitabilityFormRows => {
  const defaultRows = FIELD.PROFITABILITY
    .rows as unknown as ProfitabilityFormRows;

  return defaultRows.map((defaultRow, index) => ({
    ...defaultRow,
    ...(rows?.[index] ?? {}),
    category: rows?.[index]?.category || defaultRow.category,
  })) as ProfitabilityFormRows;
};

const ContractInfoStep2 = ({ tempDocumentDetail }: ContractInfoProps) => {
  const {
    register,
    setValue,
    setError,
    clearErrors,
    control,
    watch,
    formState: { errors },
  } = useFormContext<ContractInfoStep2Form>();

  // 금액 단위 상태 관리 (각 행별로)
  const [amountUnits, setAmountUnits] =
    useState<Record<string, string>>(DEFAULT_AMOUNT_UNITS);

  const profitabilityRows = watch(FIELD.PROFITABILITY.key);
  const savedAmountUnits = watch(FIELD.PROFITABILITY_UNITS.key);
  const sharedAmountUnit = amountUnits["0"] || DEFAULT_AMOUNT_UNITS["0"];

  const unitOptions = [
    { label: "원", value: "1" },
    { label: "천 원", value: "1000" },
    { label: "백만 원", value: "1000000" },
  ];

  /**
   * @description 실제 값을 단위에 따른 표시 값으로 변환
   */
  const getDisplayValue = (actualValue: number, unit: string): string => {
    if (!actualValue) return "";
    const unitValue = Number(unit);
    const displayValue = actualValue / unitValue;
    const formattedValue =
      displayValue % 1 === 0
        ? displayValue.toString()
        : displayValue.toFixed(1);
    return formatNumber(formattedValue);
  };

  /**
   * @description 표시 값과 단위를 실제 값으로 변환
   */
  const getActualValue = (displayValue: string, unit: string): number => {
    if (!displayValue) return 0;
    // 천 자리 구분자 제거
    const cleanValue = displayValue.replace(/,/g, "");
    const numericValue = parseFloat(cleanValue);
    const unitValue = Number(unit);
    return Math.round(numericValue * unitValue);
  };

  const PROFITABILITY_AMOUNT_MAX_DIGITS = 11;
  const profitabilityAmountErrorMessage = "11자리 이하 숫자만 입력 가능";

  const validateProfitabilityAmountLength = (
    fieldKey: string,
    value: string,
  ) => {
    if (value.replace(/[^0-9]/g, "").length > PROFITABILITY_AMOUNT_MAX_DIGITS) {
      setError(fieldKey as never, {
        type: "validate",
        message: profitabilityAmountErrorMessage,
      });
      return true;
    }

    clearErrors(fieldKey as never);
    return false;
  };

  const applyProfitabilityAmountValue = (fieldKey: string, value: string) => {
    const cleanValue = value.replace(/[^0-9.,-]/g, "");

    if (validateProfitabilityAmountLength(fieldKey, cleanValue)) {
      return false;
    }

    const actualValue = getActualValue(cleanValue, sharedAmountUnit);

    setValue(fieldKey as never, actualValue as never, {
      shouldValidate: false,
      shouldDirty: true,
    });

    return true;
  };

  /**
   * @description 단위 변경 핸들러
   */
  const handleUnitChange = (_rowIndex: number, newUnit: string) => {
    const nextUnits = normalizeAmountUnits({ 0: newUnit });
    setAmountUnits(nextUnits);
    setValue(FIELD.PROFITABILITY_UNITS.key, nextUnits, {
      shouldDirty: true,
    });
  };

  /**
   * @description 합계 계산 함수
   */
  const calcTotalAmount = () => {
    const landCost = Number(
      watch(
        `${FIELD.PROFITABILITY.key}.1.${FIELD.PROFITABILITY.columns[1].key}`,
      ),
    );
    const constructionCost = Number(
      watch(
        `${FIELD.PROFITABILITY.key}.2.${FIELD.PROFITABILITY.columns[1].key}`,
      ),
    );
    const otherCosts = Number(
      watch(
        `${FIELD.PROFITABILITY.key}.3.${FIELD.PROFITABILITY.columns[1].key}`,
      ),
    );

    const totalAmount = landCost + constructionCost + otherCosts;

    setValue(
      `${FIELD.PROFITABILITY.key}.4.${FIELD.PROFITABILITY.columns[1].key}`,
      totalAmount,
    );
  };

  useEffect(() => {
    const normalizedRows = normalizeProfitabilityRows(profitabilityRows);

    const needsNormalization =
      !profitabilityRows?.length ||
      profitabilityRows.length !== normalizedRows.length ||
      normalizedRows.some(
        (row, index) => profitabilityRows[index]?.category !== row.category,
      );

    if (needsNormalization) {
      setValue(FIELD.PROFITABILITY.key, normalizedRows, {
        shouldDirty: false,
      });
    }
  }, [profitabilityRows, setValue]);

  useEffect(() => {
    const normalizedUnits = normalizeAmountUnits(
      (savedAmountUnits as Record<string, string> | undefined) ||
        (tempDocumentDetail?.documentData?.[FIELD.PROFITABILITY_UNITS.key] as
          | Record<string, string>
          | undefined),
    );

    setAmountUnits(normalizedUnits);

    const needsUnitSync =
      !savedAmountUnits ||
      Object.keys(DEFAULT_AMOUNT_UNITS).some(
        (key) => savedAmountUnits?.[key] !== normalizedUnits[key],
      );

    if (needsUnitSync) {
      setValue(FIELD.PROFITABILITY_UNITS.key, normalizedUnits, {
        shouldDirty: false,
      });
    }
  }, [savedAmountUnits, setValue, tempDocumentDetail]);

  return (
    <CreateDocumentClassFormBody noBorderTop>
      {/* 기본검토 */}
      <GroupTitleStyle>{FIELD.BASIC_REVIEW.label}</GroupTitleStyle>

      <CreateDocumentForm.Field
        label={FIELD.BASIC_REVIEW.fields.LOCATION.label}
      >
        <DocumentTextAreaField
          placeholder={FIELD.BASIC_REVIEW.fields.LOCATION.placeholder}
          {...register(FIELD.BASIC_REVIEW.fields.LOCATION.key, {
            minLength: {
              value: FIELD.BASIC_REVIEW.fields.LOCATION.minLength,
              message: formatMinMaxLengthMessage(
                FIELD.BASIC_REVIEW.fields.LOCATION.minLength,
                FIELD.BASIC_REVIEW.fields.LOCATION.maxLength,
              ),
            },
            maxLength: {
              value: FIELD.BASIC_REVIEW.fields.LOCATION.maxLength,
              message: formatMinMaxLengthMessage(
                FIELD.BASIC_REVIEW.fields.LOCATION.minLength,
                FIELD.BASIC_REVIEW.fields.LOCATION.maxLength,
              ),
            },
          })}
          errorMessage={errors[FIELD.BASIC_REVIEW.fields.LOCATION.key]?.message}
        />
      </CreateDocumentForm.Field>
      <CreateDocumentForm.Field
        label={FIELD.BASIC_REVIEW.fields.BUSINESS_RIGHT_ACQUISITION.label}
      >
        <DocumentTextAreaField
          placeholder={
            FIELD.BASIC_REVIEW.fields.BUSINESS_RIGHT_ACQUISITION.placeholder
          }
          {...register(
            FIELD.BASIC_REVIEW.fields.BUSINESS_RIGHT_ACQUISITION.key,
            {
              minLength: {
                value:
                  FIELD.BASIC_REVIEW.fields.BUSINESS_RIGHT_ACQUISITION
                    .minLength,
                message: formatMinMaxLengthMessage(
                  FIELD.BASIC_REVIEW.fields.BUSINESS_RIGHT_ACQUISITION
                    .minLength,
                  FIELD.BASIC_REVIEW.fields.BUSINESS_RIGHT_ACQUISITION
                    .maxLength,
                ),
              },
              maxLength: {
                value:
                  FIELD.BASIC_REVIEW.fields.BUSINESS_RIGHT_ACQUISITION
                    .maxLength,
                message: formatMinMaxLengthMessage(
                  FIELD.BASIC_REVIEW.fields.BUSINESS_RIGHT_ACQUISITION
                    .minLength,
                  FIELD.BASIC_REVIEW.fields.BUSINESS_RIGHT_ACQUISITION
                    .maxLength,
                ),
              },
            },
          )}
          errorMessage={
            errors[FIELD.BASIC_REVIEW.fields.BUSINESS_RIGHT_ACQUISITION.key]
              ?.message
          }
        />
      </CreateDocumentForm.Field>

      <CreateDocumentForm.Field
        label={FIELD.BASIC_REVIEW.fields.PERMISSION.label}
      >
        <DocumentTextAreaField
          placeholder={FIELD.BASIC_REVIEW.fields.PERMISSION.placeholder}
          {...register(FIELD.BASIC_REVIEW.fields.PERMISSION.key, {
            minLength: {
              value: FIELD.BASIC_REVIEW.fields.PERMISSION.minLength,
              message: formatMinMaxLengthMessage(
                FIELD.BASIC_REVIEW.fields.PERMISSION.minLength,
                FIELD.BASIC_REVIEW.fields.PERMISSION.maxLength,
              ),
            },
            maxLength: {
              value: FIELD.BASIC_REVIEW.fields.PERMISSION.maxLength,
              message: formatMinMaxLengthMessage(
                FIELD.BASIC_REVIEW.fields.PERMISSION.minLength,
                FIELD.BASIC_REVIEW.fields.PERMISSION.maxLength,
              ),
            },
          })}
          errorMessage={
            errors[FIELD.BASIC_REVIEW.fields.PERMISSION.key]?.message
          }
        />
      </CreateDocumentForm.Field>

      <CreateDocumentForm.Field
        label={FIELD.BASIC_REVIEW.fields.MARKET_STATUS.label}
      >
        <DocumentTextAreaField
          placeholder={FIELD.BASIC_REVIEW.fields.MARKET_STATUS.placeholder}
          {...register(FIELD.BASIC_REVIEW.fields.MARKET_STATUS.key, {
            minLength: {
              value: FIELD.BASIC_REVIEW.fields.MARKET_STATUS.minLength,
              message: formatMinMaxLengthMessage(
                FIELD.BASIC_REVIEW.fields.MARKET_STATUS.minLength,
                FIELD.BASIC_REVIEW.fields.MARKET_STATUS.maxLength,
              ),
            },
            maxLength: {
              value: FIELD.BASIC_REVIEW.fields.MARKET_STATUS.maxLength,
              message: formatMinMaxLengthMessage(
                FIELD.BASIC_REVIEW.fields.MARKET_STATUS.minLength,
                FIELD.BASIC_REVIEW.fields.MARKET_STATUS.maxLength,
              ),
            },
          })}
          errorMessage={
            errors[FIELD.BASIC_REVIEW.fields.MARKET_STATUS.key]?.message
          }
        />
      </CreateDocumentForm.Field>

      <CreateDocumentForm.Field
        label={FIELD.BASIC_REVIEW.fields.APPROPRIATE_SALE_PRICE.label}
      >
        <DocumentTextAreaField
          placeholder={
            FIELD.BASIC_REVIEW.fields.APPROPRIATE_SALE_PRICE.placeholder
          }
          {...register(FIELD.BASIC_REVIEW.fields.APPROPRIATE_SALE_PRICE.key, {
            minLength: {
              value: FIELD.BASIC_REVIEW.fields.APPROPRIATE_SALE_PRICE.minLength,
              message: formatMinMaxLengthMessage(
                FIELD.BASIC_REVIEW.fields.APPROPRIATE_SALE_PRICE.minLength,
                FIELD.BASIC_REVIEW.fields.APPROPRIATE_SALE_PRICE.maxLength,
              ),
            },
            maxLength: {
              value: FIELD.BASIC_REVIEW.fields.APPROPRIATE_SALE_PRICE.maxLength,
              message: formatMinMaxLengthMessage(
                FIELD.BASIC_REVIEW.fields.APPROPRIATE_SALE_PRICE.minLength,
                FIELD.BASIC_REVIEW.fields.APPROPRIATE_SALE_PRICE.maxLength,
              ),
            },
          })}
          errorMessage={
            errors[FIELD.BASIC_REVIEW.fields.APPROPRIATE_SALE_PRICE.key]
              ?.message
          }
        />
      </CreateDocumentForm.Field>

      <GrouopDividerStyle />

      {/* 사업성 */}
      <GroupTitleStyle>{FIELD.PROFITABILITY.label}</GroupTitleStyle>

      <CreateDocumentForm.Field label={FIELD.PROFITABILITY.rows[0].category}>
        <ProfitabilityRowStyle>
          <DocumentTextField
            placeholder="0"
            inputStyle={{ textAlign: "right" }}
            value={getDisplayValue(
              watch(
                `${FIELD.PROFITABILITY.key}.0.${FIELD.PROFITABILITY.columns[1].key}`,
              ) || 0,
              sharedAmountUnit,
            )}
            {...register(
              `${FIELD.PROFITABILITY.key}.0.${FIELD.PROFITABILITY.columns[1].key}`,
              {
                valueAsNumber: true,
                minLength: {
                  value: FIELD.PROFITABILITY.columns[1].minLength,
                  message: formatMinMaxLengthMessage(
                    FIELD.PROFITABILITY.columns[1].minLength,
                    FIELD.PROFITABILITY.columns[1].maxLength,
                  ),
                },
                maxLength: {
                  value: FIELD.PROFITABILITY.columns[1].maxLength,
                  message: formatMinMaxLengthMessage(
                    FIELD.PROFITABILITY.columns[1].minLength,
                    FIELD.PROFITABILITY.columns[1].maxLength,
                  ),
                },
                min: {
                  value: FIELD.PROFITABILITY.columns[1].min,
                  message: "0 이상의 숫자만 입력 가능",
                },
                onChange: (event) => {
                  const value = event.target.value;
                  // 소수점과 천 자리 구분자 포함 숫자만 허용
                  const cleanValue = value.replace(/[^0-9.,-]/g, "");
                  if (
                    validateProfitabilityAmountLength(
                      `${FIELD.PROFITABILITY.key}.0.${FIELD.PROFITABILITY.columns[1].key}`,
                      cleanValue,
                    )
                  )
                    return;
                  const actualValue = getActualValue(
                    cleanValue,
                    sharedAmountUnit,
                  );
                  setValue(
                    `${FIELD.PROFITABILITY.key}.0.${FIELD.PROFITABILITY.columns[1].key}`,
                    actualValue,
                    { shouldValidate: true },
                  );
                },
                onBlur: (event) => {
                  const value = event.target.value;
                  const cleanValue = value.replace(/[^0-9.,-]/g, "");
                  if (
                    validateProfitabilityAmountLength(
                      `${FIELD.PROFITABILITY.key}.0.${FIELD.PROFITABILITY.columns[1].key}`,
                      cleanValue,
                    )
                  )
                    return;
                  const actualValue = getActualValue(
                    cleanValue,
                    sharedAmountUnit,
                  );
                  setValue(
                    `${FIELD.PROFITABILITY.key}.0.${FIELD.PROFITABILITY.columns[1].key}`,
                    actualValue,
                    { shouldValidate: true },
                  );
                },
                validate: (value) => {
                  if (value < 0) return "0 이상의 금액 입력";
                  const displayValue = getDisplayValue(
                    Number(value) || 0,
                    sharedAmountUnit,
                  );
                  if (
                    validateProfitabilityAmountLength(
                      `${FIELD.PROFITABILITY.key}.0.${FIELD.PROFITABILITY.columns[1].key}`,
                      displayValue,
                    )
                  ) {
                    return profitabilityAmountErrorMessage;
                  }
                  return true;
                },
              },
            )}
            onChange={(event) => {
              applyProfitabilityAmountValue(
                `${FIELD.PROFITABILITY.key}.0.${FIELD.PROFITABILITY.columns[1].key}`,
                event.target.value,
              );
            }}
            onBlur={(event) => {
              applyProfitabilityAmountValue(
                `${FIELD.PROFITABILITY.key}.0.${FIELD.PROFITABILITY.columns[1].key}`,
                event.target.value,
              );
            }}
            errorMessage={
              get(
                errors,
                `${FIELD.PROFITABILITY.key}.0.${FIELD.PROFITABILITY.columns[1].key}`,
              )?.message
            }
          />
          <Select
            style={{ width: "12rem", height: "5.5rem" }}
            list={unitOptions}
            value={sharedAmountUnit}
            onValueChange={(value) => handleUnitChange(0, value)}
          />
          <DocumentTextField
            placeholder="비고"
            type={FIELD.PROFITABILITY.columns[2].type}
            {...register(
              `${FIELD.PROFITABILITY.key}.0.${FIELD.PROFITABILITY.columns[2].key}`,
              {
                minLength: {
                  value: FIELD.PROFITABILITY.columns[2].minLength,
                  message: formatMinMaxLengthMessage(
                    FIELD.PROFITABILITY.columns[2].minLength,
                    FIELD.PROFITABILITY.columns[2].maxLength,
                  ),
                },
                maxLength: {
                  value: FIELD.PROFITABILITY.columns[2].maxLength,
                  message: formatMinMaxLengthMessage(
                    FIELD.PROFITABILITY.columns[2].minLength,
                    FIELD.PROFITABILITY.columns[2].maxLength,
                  ),
                },
              },
            )}
            errorMessage={
              get(
                errors,
                `${FIELD.PROFITABILITY.key}.0.${FIELD.PROFITABILITY.columns[2].key}`,
              )?.message
            }
          />
        </ProfitabilityRowStyle>
      </CreateDocumentForm.Field>

      <CreateDocumentForm.Field
        label="매출원가"
        bodyStyle={{ display: "flex", flexDirection: "column", gap: "2rem" }}
      >
        {FIELD.PROFITABILITY.rows.slice(1, 4).map((row, index) => {
          const rowIndex = index + 1;

          const amountKey =
            `${FIELD.PROFITABILITY.key}.${rowIndex}.${FIELD.PROFITABILITY.columns[1].key}` as const;
          const noteKey =
            `${FIELD.PROFITABILITY.key}.${rowIndex}.${FIELD.PROFITABILITY.columns[2].key}` as const;

          return (
            <CreateDocumentForm.Field key={row.category} label={row.category}>
              <ProfitabilityRowStyle>
                <DocumentTextField
                  placeholder="0"
                  inputStyle={{ textAlign: "right" }}
                  value={getDisplayValue(
                    watch(amountKey) || 0,
                    sharedAmountUnit,
                  )}
                  {...register(amountKey, {
                    minLength: {
                      value: FIELD.PROFITABILITY.columns[1].minLength,
                      message: formatMinMaxLengthMessage(
                        FIELD.PROFITABILITY.columns[1].minLength,
                        FIELD.PROFITABILITY.columns[1].maxLength,
                      ),
                    },
                    maxLength: {
                      value: FIELD.PROFITABILITY.columns[1].maxLength,
                      message: formatMinMaxLengthMessage(
                        FIELD.PROFITABILITY.columns[1].minLength,
                        FIELD.PROFITABILITY.columns[1].maxLength,
                      ),
                    },
                    min: {
                      value: FIELD.PROFITABILITY.columns[1].min,
                      message: "0이상의 숫자만 입력 가능",
                    },
                    onChange: (event) => {
                      const value = event.target.value;
                      const cleanValue = value.replace(/[^0-9.,-]/g, "");
                      if (
                        validateProfitabilityAmountLength(amountKey, cleanValue)
                      )
                        return;
                      const actualValue = getActualValue(
                        cleanValue,
                        sharedAmountUnit,
                      );
                      setValue(amountKey, actualValue, {
                        shouldValidate: true,
                      });
                    },
                    onBlur: (event) => {
                      const value = event.target.value;
                      const cleanValue = value.replace(/[^0-9.,-]/g, "");
                      if (
                        validateProfitabilityAmountLength(amountKey, cleanValue)
                      )
                        return;
                      const actualValue = getActualValue(
                        cleanValue,
                        sharedAmountUnit,
                      );
                      setValue(amountKey, actualValue, {
                        shouldValidate: true,
                      });
                      calcTotalAmount();
                    },
                    validate: (value) => {
                      if (value < 0) return "0 이상의 금액 입력";
                      return true;
                    },
                  })}
                  onChange={(event) => {
                    applyProfitabilityAmountValue(
                      amountKey,
                      event.target.value,
                    );
                  }}
                  onBlur={(event) => {
                    const applied = applyProfitabilityAmountValue(
                      amountKey,
                      event.target.value,
                    );
                    if (applied) {
                      calcTotalAmount();
                    }
                  }}
                  errorMessage={get(errors, amountKey)?.message}
                />
                <Select
                  style={{ width: "12rem", height: "5.5rem" }}
                  list={unitOptions}
                  value={sharedAmountUnit}
                  onValueChange={(value) => handleUnitChange(rowIndex, value)}
                />
                <DocumentTextField
                  placeholder="비고"
                  type={FIELD.PROFITABILITY.columns[2].type}
                  {...register(noteKey, {
                    minLength: {
                      value: FIELD.PROFITABILITY.columns[2].minLength,
                      message: formatMinMaxLengthMessage(
                        FIELD.PROFITABILITY.columns[2].minLength,
                        FIELD.PROFITABILITY.columns[2].maxLength,
                      ),
                    },
                    maxLength: {
                      value: FIELD.PROFITABILITY.columns[2].maxLength,
                      message: formatMinMaxLengthMessage(
                        FIELD.PROFITABILITY.columns[2].minLength,
                        FIELD.PROFITABILITY.columns[2].maxLength,
                      ),
                    },
                  })}
                  errorMessage={get(errors, noteKey)?.message}
                />
              </ProfitabilityRowStyle>
            </CreateDocumentForm.Field>
          );
        })}

        <Divider />

        {/* 계 */}
        <CreateDocumentForm.Field label={FIELD.PROFITABILITY.rows[4].category}>
          <ProfitabilityRowStyle>
            <DocumentTextField
              placeholder="0"
              inputStyle={{ textAlign: "right" }}
              value={getDisplayValue(
                watch(
                  `${FIELD.PROFITABILITY.key}.4.${FIELD.PROFITABILITY.columns[1].key}`,
                ) || 0,
                sharedAmountUnit,
              )}
              readOnly
              {...register(
                `${FIELD.PROFITABILITY.key}.4.${FIELD.PROFITABILITY.columns[1].key}`,
                {
                  minLength: FIELD.PROFITABILITY.columns[1].minLength,
                  maxLength: FIELD.PROFITABILITY.columns[1].maxLength,
                  valueAsNumber: true,
                },
              )}
            />
            <Select
              style={{ width: "12rem", height: "5.5rem" }}
              list={unitOptions}
              value={sharedAmountUnit}
              onValueChange={(value) => handleUnitChange(4, value)}
            />
          </ProfitabilityRowStyle>
        </CreateDocumentForm.Field>
      </CreateDocumentForm.Field>

      <Divider />

      <CreateDocumentForm.Field
        label="사업이익"
        bodyStyle={{ display: "flex", flexDirection: "column", gap: "2rem" }}
      >
        {FIELD.PROFITABILITY.rows.slice(5, 7).map((row, index) => {
          const rowIndex = index + 5;

          const amountKey =
            `${FIELD.PROFITABILITY.key}.${rowIndex}.${FIELD.PROFITABILITY.columns[1].key}` as const;
          const noteKey =
            `${FIELD.PROFITABILITY.key}.${rowIndex}.${FIELD.PROFITABILITY.columns[2].key}` as const;

          return (
            <CreateDocumentForm.Field key={row.category} label={row.category}>
              <ProfitabilityRowStyle>
                <DocumentTextField
                  placeholder="0"
                  inputStyle={{ textAlign: "right" }}
                  value={getDisplayValue(
                    watch(amountKey) || 0,
                    sharedAmountUnit,
                  )}
                  {...register(amountKey, {
                    minLength: {
                      value: FIELD.PROFITABILITY.columns[1].minLength,
                      message: formatMinMaxLengthMessage(
                        FIELD.PROFITABILITY.columns[1].minLength,
                        FIELD.PROFITABILITY.columns[1].maxLength,
                      ),
                    },
                    maxLength: {
                      value: FIELD.PROFITABILITY.columns[1].maxLength,
                      message: formatMinMaxLengthMessage(
                        FIELD.PROFITABILITY.columns[1].minLength,
                        FIELD.PROFITABILITY.columns[1].maxLength,
                      ),
                    },
                    min: {
                      value: FIELD.PROFITABILITY.columns[1].min,
                      message: "0이상의 숫자만 입력 가능",
                    },
                    onChange: (event) => {
                      const value = event.target.value;
                      const cleanValue = value.replace(/[^0-9.,-]/g, "");
                      const actualValue = getActualValue(
                        cleanValue,
                        sharedAmountUnit,
                      );
                      setValue(amountKey, actualValue, {
                        shouldValidate: true,
                      });
                    },
                    onBlur: (event) => {
                      const value = event.target.value;
                      const cleanValue = value.replace(/[^0-9.,-]/g, "");
                      const actualValue = getActualValue(
                        cleanValue,
                        sharedAmountUnit,
                      );
                      setValue(amountKey, actualValue, {
                        shouldValidate: true,
                      });
                    },
                    validate: (value) => {
                      if (value < 0) return "0 이상의 금액 입력";
                      return true;
                    },
                  })}
                  onChange={(event) => {
                    applyProfitabilityAmountValue(
                      amountKey,
                      event.target.value,
                    );
                  }}
                  onBlur={(event) => {
                    applyProfitabilityAmountValue(
                      amountKey,
                      event.target.value,
                    );
                  }}
                  errorMessage={get(errors, amountKey)?.message}
                />
                <Select
                  style={{ width: "12rem", height: "5.5rem" }}
                  list={unitOptions}
                  value={sharedAmountUnit}
                  onValueChange={(value) => handleUnitChange(rowIndex, value)}
                />
                <DocumentTextField
                  placeholder="비고"
                  type={FIELD.PROFITABILITY.columns[2].type}
                  {...register(noteKey, {
                    minLength: {
                      value: FIELD.PROFITABILITY.columns[2].minLength,
                      message: formatMinMaxLengthMessage(
                        FIELD.PROFITABILITY.columns[2].minLength,
                        FIELD.PROFITABILITY.columns[2].maxLength,
                      ),
                    },
                    maxLength: {
                      value: FIELD.PROFITABILITY.columns[2].maxLength,
                      message: formatMinMaxLengthMessage(
                        FIELD.PROFITABILITY.columns[2].minLength,
                        FIELD.PROFITABILITY.columns[2].maxLength,
                      ),
                    },
                  })}
                  errorMessage={get(errors, noteKey)?.message}
                />
              </ProfitabilityRowStyle>
            </CreateDocumentForm.Field>
          );
        })}
      </CreateDocumentForm.Field>

      <CreateDocumentForm.Field label={FIELD.REVIEW_RESULT.label}>
        <DocumentTextAreaField
          placeholder={FIELD.REVIEW_RESULT.placeholder}
          required={FIELD.REVIEW_RESULT.required}
          {...register(FIELD.REVIEW_RESULT.key, {
            required: "검토결과 입력 필수",
            minLength: {
              value: FIELD.REVIEW_RESULT.minLength,
              message: `${FIELD.REVIEW_RESULT.minLength}자 이상 입력 가능`,
            },
          })}
          errorMessage={get(errors, FIELD.REVIEW_RESULT.key)?.message}
        />
      </CreateDocumentForm.Field>

      <CreateDocumentForm.Field label={FIELD.PROCESSING_RESULT.label}>
        <CheckboxGroupStyle>
          <div>
            {FIELD.PROCESSING_RESULT.options.map((type) => {
              return (
                <Controller
                  key={type}
                  name={FIELD.PROCESSING_RESULT.key}
                  control={control}
                  render={() => (
                    <CheckBox
                      value={type}
                      {...register(FIELD.PROCESSING_RESULT.key, {
                        required: "처리결과 입력 필수",
                      })}
                      checked={watch(FIELD.PROCESSING_RESULT.key) === type}
                      onCheckedChange={() => {
                        setValue(FIELD.PROCESSING_RESULT.key, type);
                      }}
                    >
                      {type}
                    </CheckBox>
                  )}
                />
              );
            })}
          </div>

          {get(errors, FIELD.PROCESSING_RESULT.key)?.message && (
            <label>{get(errors, FIELD.PROCESSING_RESULT.key)?.message}</label>
          )}
        </CheckboxGroupStyle>
      </CreateDocumentForm.Field>
    </CreateDocumentClassFormBody>
  );
};

export default ContractInfoStep2;

/* 스타일 */
const GroupTitleStyle = styled.h3`
  ${({ theme }) => theme.typography.t1.semibold}
  letter-spacing: -0.06rem;
`;

const GrouopDividerStyle = styled(Divider)`
  margin: 1rem 0;
`;

const ProfitabilityRowStyle = styled.div`
  display: flex;
  align-items: baseline;
  gap: 1rem;
`;

const CheckboxGroupStyle = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;

  & > div {
    display: flex;
    gap: 2.4rem;
  }

  & > label {
    padding-left: 1.6rem;
    ${({ theme }) => theme.typography.body3.regular};
    color: ${({ theme }) => theme.color.text.danger};
  }
`;
